import React, { useEffect, useState } from 'react';
import { BookOpen, Search, Database, ExternalLink, FileText, Link as LinkIcon } from 'lucide-react';

const LibraryPage = ({ initialTab = 'databases', pageTitle = 'The Federal Polytechnic Library', pageDescription = 'Access thousands of books, journals, databases, and digital resources for your academic success', searchPreset = '' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState(searchPreset);

  useEffect(() => {
    setActiveTab(initialTab);
    setSearchQuery(searchPreset);
  }, [initialTab, searchPreset]);

  // Databases data
  const databases = [
    {
      name: "Federal Polytechnic Bida Institutional Repository",
      description: "Collection including eBook Academic Collection, Academic Search Complete, Journals, and more!!!",
      url: "http://repository.fedpolybida.edu.ng:4000",
      type: "Institutional Repository"
    },
    {
      name: "EbscoHost Databases",
      description: "Collection including eBook Academic Collection, Academic Search Complete, Audiobook Collection, and GreenFILE",
      url: "http://search.ebscohost.com/login.aspx",
      type: "Multi-disciplinary"
    },
    {
      name: "Ebrary",
      description: "More than 84,000 e-Books including handbooks, manuals, reports and authoritative documents",
      url: "http://site.ebrary.com/lib/aunigeria",
      type: "eBooks"
    },
    {
      name: "JSTOR",
      description: "Full-text issues of core journals in humanities, social sciences and sciences",
      url: "http://www.jstor.org/",
      type: "Journal Archive"
    },
    {
      name: "Project Muse",
      description: "100% full-text access to prestigious humanities and social sciences journals",
      url: "http://muse.jhu.edu/",
      type: "Journals"
    },
    {
      name: "IEEE Xplore – IBM Journal",
      description: "Peer-reviewed technical journal featuring work in science, technology and engineering",
      url: "https://ieeexplore.ieee.org/xpl/RecentIssue.jsp?punumber=5288520",
      type: "Technology"
    },
    {
      name: "Britannica Academic",
      description: "Multi-disciplinary reference tool with articles, maps, flags, and academic resources",
      url: "https://academic.eb.com/levels/collegiate",
      type: "Reference"
    },
    {
      name: "HeinOnline",
      description: "Over 100 million pages of legal history in fully-searchable, image-based format",
      url: "https://heinonline.org/HOL/login-hol",
      type: "Legal"
    },
    {
      name: "Law Pavilion",
      description: "Legal IT Research Tools for Lawyers, Judges and Law firms",
      url: "http://www.lawpavilionplus.com/",
      type: "Legal"
    },
    {
      name: "ScienceDirect",
      description: "Economics, Econometrics and Finance publications from Elsevier",
      url: "https://legalpediaresources.com/",
      type: "Economics"
    },
    {
      name: "Emerald Insight",
      description: "Journals, books and teaching cases across multiple disciplines",
      url: "http://www.emeraldinsight.com/action/showPublications",
      type: "Multi-disciplinary"
    },
    {
      name: "ACM Digital Library",
      description: "Premier resource for computing field publications and conferences",
      url: "http://portal.acm.org/dl.cfm",
      type: "Computer Science"
    },
    {
      name: "World Bank e-Library",
      description: "Nearly 8,000 books, reports, journals, and working papers",
      url: "http://elibrary.worldbank.org/",
      type: "Economics"
    },
    {
      name: "African Journals Online (AJOL)",
      description: "Database of African-published journals across academic disciplines",
      url: "https://www.ajol.info/index.php/ajol",
      type: "African Studies"
    },
    {
      name: "Oxford English Dictionary (OED)",
      description: "The largest dictionary of any language with word evolution over 1500 years",
      url: "http://www.oed.com/",
      type: "Reference"
    },
    {
      name: "WorldCat.org",
      description: "The World's Largest Library Catalog",
      url: "https://americanunivofnigeria.on.worldcat.org/discovery",
      type: "Catalog"
    }
  ];

  // Open Access resources
  const openAccess = [
    {
      name: "Open Access Theses and Dissertations (OATD)",
      description: "Resource for finding open access graduate theses and dissertations worldwide",
      url: "http://www.oatd.org/",
      category: "Theses"
    },
    {
      name: "Cambridge Open Access",
      description: "Open Access Journals from Cambridge University Press",
      url: "https://www.cambridge.org/core/what-we-publish/open-access",
      category: "Journals"
    },
    {
      name: "Academic Journals",
      description: "African scholars free access to over 600,000 high-quality peer-reviewed articles",
      url: "https://academicjournals.org/",
      category: "African Research"
    },
    {
      name: "SAGE Premier Journals",
      description: "824 titles across business, humanities, social science, and STEM",
      url: "https://us.sagepub.com/en-us/nam/pure-gold-open-access-journals",
      category: "Multi-disciplinary"
    },
    {
      name: "Directory of Open Access Books (DOAB)",
      description: "Discovery service for peer reviewed books published under open access",
      url: "http://www.doabooks.org/",
      category: "eBooks"
    },
    {
      name: "EconBiz",
      description: "Search engine for economic information and business studies",
      url: "https://www.econbiz.de/",
      category: "Economics"
    },
    {
      name: "arXiv",
      description: "Open access e-prints in Physics, Mathematics, Computer Science, and more",
      url: "https://arxiv.org/",
      category: "STEM"
    },
    {
      name: "JSTOR Open Content",
      description: "Free journal articles and open access book chapters",
      url: "https://www.jstor.org/open/",
      category: "Multi-disciplinary"
    },
    {
      name: "Project MUSE Open Access",
      description: "Open access ebooks and ejournals from university presses",
      url: "https://muse.jhu.edu/open-access",
      category: "Humanities"
    },
    {
      name: "Biomed Central Open Access",
      description: "300+ peer-reviewed open access journals in science and medicine",
      url: "https://www.biomedcentral.com/",
      category: "Medical"
    }
  ];

  // Citation and reference tools
  const citationTools = [
    {
      name: "APA Formatting and Style Guide",
      description: "From the Online Writing Lab (OWL) at Purdue University",
      url: "https://owl.purdue.edu/owl/research_and_citation/apa_style/apa_formatting_and_style_guide/general_format.html",
      type: "APA"
    },
    {
      name: "Citation Machine",
      description: "Automatically generates citations in MLA, APA, Chicago, Turabian, and more",
      url: "https://www.citationmachine.net/",
      type: "Multi-format"
    },
    {
      name: "APA Citation Generator",
      description: "Specialized tool for APA citation formatting",
      url: "https://www.apa.org/apa-style/online-citation-generator",
      type: "APA"
    },
    {
      name: "MLA Formatting Guide",
      description: "Complete guide to Modern Language Association citation style",
      url: "https://owl.purdue.edu/owl/research_and_citation/mla_style/mla_formatting_and_style_guide/mla_general_format.html",
      type: "MLA"
    },
    {
      name: "Zotero",
      description: "Free, easy-to-use tool to help you collect, organize, cite, and share research",
      url: "https://www.zotero.org/",
      type: "Reference Manager"
    },
    {
      name: "Mendeley",
      description: "Reference manager and academic social network",
      url: "https://www.mendeley.com/",
      type: "Reference Manager"
    }
  ];

  // Filter resources based on search query
  const filterResources = (resources) => {
    if (!searchQuery) return resources;
    return resources.filter(resource => 
      resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (resource.type && resource.type.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (resource.category && resource.category.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{pageTitle}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {pageDescription}
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-4 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search databases, resources, and tools..."
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
        {[
          { id: 'databases', name: 'Databases' },
          { id: 'openaccess', name: 'Open Access' },
          { id: 'citation', name: 'Citation Tools' },
          { id: 'hours', name: 'Opening Hours' },
          { id: 'services', name: 'Services' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-4 px-6 font-medium ${activeTab === tab.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.name}
          </button>
        ))}
      </div>

        {/* Tab Content */}
        {activeTab === 'databases' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Research Databases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filterResources(databases).map((db, index) => (
                <a
                  key={index}
                  href={db.url}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-transparent hover:border-blue-200 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Database className="w-6 h-6 text-blue-600" />
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{db.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{db.description}</p>
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                    {db.type}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'openaccess' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Open Access Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filterResources(openAccess).map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-transparent hover:border-blue-200 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <LinkIcon className="w-6 h-6 text-green-600" />
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{resource.description}</p>
                  <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                    {resource.category}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'citation' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Citation & Reference Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filterResources(citationTools).map((tool, index) => (
                <a
                  key={index}
                  href={tool.url}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-transparent hover:border-blue-200 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="w-6 h-6 text-purple-600" />
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{tool.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{tool.description}</p>
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded">
                    {tool.type}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}


        {activeTab === 'hours' && (
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Library Opening Hours</h2>
            <div className="space-y-4">
              {[
                { day: "Monday - Friday", hours: "8:00 AM - 8:00 PM" },
                { day: "Saturday", hours: "9:00 AM - 5:00 PM" },
                { day: "Sunday", hours: "10:00 AM - 4:00 PM" },
                { day: "Public Holidays", hours: "Closed" }
              ].map((schedule, index) => (
                <div key={index} className="flex justify-between py-3 border-b border-gray-100">
                  <span className="font-medium">{schedule.day}</span>
                  <span>{schedule.hours}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Library Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: "Book Borrowing",
                  description: "Borrow books for up to 2 weeks with possible renewal"
                },
                {
                  title: "Research Assistance",
                  description: "Get help with your research from our librarians"
                },
                {
                  title: "Study Rooms",
                  description: "Reserve private study rooms for group work"
                },
                {
                  title: "Interlibrary Loan",
                  description: "Request books and articles from other libraries"
                }
              ].map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">50+</div>
            <div className="text-gray-600">Research Databases</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">100+</div>
            <div className="text-gray-600">Open Access Resources</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">24/7</div>
            <div className="text-gray-600">Online Access</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">500+</div>
            <div className="text-gray-600">Study Spaces</div>
          </div>
        </div>

        {/* Support Section */}
        <div className="mt-12 bg-blue-50 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Research Help?</h2>
          <p className="text-gray-700 mb-6">
            Our librarians are available to assist you with database access, research strategies, and citation help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <a href="mailto:library@polytechnic.edu" className="text-blue-600 hover:text-blue-800 font-medium">
              library@polytechnic.edu
            </a>
            <span className="text-gray-400 hidden sm:block">•</span>
            <a href="tel:+234123456789" className="text-blue-600 hover:text-blue-800 font-medium">
              +234 123 456 789
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryPage;